import { AxiosResponse } from 'axios';

import { IDocument } from '../../shared/ts/document';
import { IGuest, IGuestCreated } from '../../shared/ts/user';
import { LandlordBooking } from '../../types/bookings';
import { PaginatedResponse } from '../../types/utils';
import httpRequest from '../httpRequest';

const baseURL = process.env.REACT_APP_GW_API_URL;

const bookingsAPI = {
  getLandlordBookings: async ({
    bookingsStatusFilters,
    page,
    per_page,
  }: {
    bookingsStatusFilters: string;
    page: number;
    per_page: number;
  }) => {
    const { data } = await httpRequest<
      PaginatedResponse<Array<LandlordBooking>>
    >({
      baseURL: baseURL,
      url: `/dalgona/landlord/bookings/`,
      method: 'GET',
      params: {
        p: { page, per_page },
        ...(bookingsStatusFilters && { f: { status: bookingsStatusFilters } }),
      },
    });

    return data;
  },

  acceptLandLordBooking: async ({ bookingId }: { bookingId: string }) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/landlord/bookings/${bookingId}/accept`,
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  counterOfferLandlordBooking: async ({
    bookingId,
    counter_offer,
    from_date,
    message_for_tenant,
  }: {
    bookingId: string;
    counter_offer: number;
    from_date: string;
    message_for_tenant: string;
  }) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/landlord/bookings/${bookingId}/counter-offers`,
      data: {
        counter_offer,
        from_date,
        message_for_tenant,
      },
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  rejectLandLordBooking: async (bookingId: string) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/landlord/bookings/${bookingId}/reject`,
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  getTenantBookings: async ({
    bookingsStatusFilters,
    page,
    per_page,
  }: {
    bookingsStatusFilters: string;
    page: number;
    per_page: number;
  }) => {
    const { data } = await httpRequest<
      PaginatedResponse<Array<LandlordBooking>>
    >({
      baseURL: baseURL,
      url: `/dalgona/tenant/bookings/`,
      method: 'GET',
      params: {
        p: { page, per_page },
        ...(bookingsStatusFilters && { f: { status: bookingsStatusFilters } }),
      },
    });

    return data;
  },

  acceptTenantCounterOffer: async (bookingId: string) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/tenant/bookings/${bookingId}/counter-offer/accept`,
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  rejectTenantCounterOffer: async (bookingId: string) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/tenant/bookings/${bookingId}/counter-offer/reject`,
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  documentUploadedForBooking: async ({ bookingId }: { bookingId: string }) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${bookingId}/documents-uploaded`,
      method: 'POST',
    })) as AxiosResponse;
    return status;
  },

  getGuestDocuments: async (
    guest_id: string,
    booking_id: string,
  ): Promise<IDocument[]> => {
    const { data } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${booking_id}/guests/${guest_id}/documents`,
      method: 'GET',
    })) as AxiosResponse;

    return data || [];
  },

  getGuests: async (booking_id: string): Promise<IGuest[]> => {
    const { data } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${booking_id}/guests`,
      method: 'GET',
    })) as AxiosResponse;

    return data.data;
  },
  createGuest: async (
    booking_id: string,
    payload: IGuest,
  ): Promise<IGuestCreated> => {
    const { data } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${booking_id}/guests`,
      method: 'POST',
      data: payload,
    })) as AxiosResponse;
    return data;
  },
  updateGuestById: async (
    booking_id: string,
    guest_id: string,
    payload: IGuest,
  ): Promise<IGuest> => {
    const { data } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${booking_id}/guests/${guest_id}`,
      method: 'PUT',
      data: payload,
    })) as AxiosResponse;

    return data.data;
  },

  getTenantBookingById: async (bookingId: string) => {
    const { status, data } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/tenant/bookings/${bookingId}`,
      method: 'GET',
    })) as AxiosResponse;
    return { status, booking: data };
  },

  deleteBookingBecauseOwnersSignatureExpired: async (params: {
    bookingId: string;
  }): Promise<void> => {
    await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/tenant/bookings/${params.bookingId}/owners-signature-expired`,
      method: 'DELETE',
    });
  },

  deleteGuestById: async (booking_id: string, user_id: number) => {
    const { status } = (await httpRequest({
      baseURL: baseURL,
      url: `/dalgona/bookings/${booking_id}/guests/${user_id}`,
      method: 'DELETE',
    })) as AxiosResponse;
    return status;
  },
};

export default bookingsAPI;
